import styled, { css } from "styled-components";
import FontSize from "../../constants/fontSize";
import COLORS from "../../constants/colors";
import { getSizeInRem } from "../../methods/common";
import { StyledButtonProps } from "./Button.types";

const getFillTypeStyleFromColor = (
    fillType: StyledButtonProps["fillType"],
    colors: {
        [key in
            | "primary"
            | "primaryDisabled"
            | "secondary"
            | "tertiary"]: string;
    },
) => {
    const { primary, primaryDisabled, secondary, tertiary } = colors;
    switch (fillType) {
        case "contained": {
            return css`
                background-color: ${primary};
                color: ${secondary};
                &:disabled {
                    background-color: ${primaryDisabled};
                }
            `;
        }
        case "outlined": {
            return css`
                background-color: ${secondary};
                border: 2px solid ${primary};
                color: ${primary};
                &:disabled {
                    border: 2px solid ${primaryDisabled};
                    color: ${primaryDisabled};
                }
            `;
        }
        case "text": {
            return css`
                background-color: unset;
                color: ${primary};
                height: unset;
                padding: 0;
                &:disabled {
                    color: ${primaryDisabled};
                }
            `;
        }
        case "tonal": {
            return css`
                background-color: ${tertiary};
                color: ${primary};
                height: unset;
                padding: 0.5em;
                &:disabled {
                    color: ${primaryDisabled};
                }
            `;
        }
    }
};
const getBtnStyle = (
    color: StyledButtonProps["color"],
    fillType: StyledButtonProps["fillType"],
) => {
    switch (color) {
        case "primary": {
            return getFillTypeStyleFromColor(fillType, {
                primary: COLORS.BLUE,
                primaryDisabled: COLORS.BLUE_300,
                secondary: COLORS.WHITE,
                tertiary: COLORS.BLUE_200,
            });
        }

        case "secondary": {
            return getFillTypeStyleFromColor(fillType, {
                primary: COLORS.GREY_1000,
                primaryDisabled: COLORS.GREY_400,
                secondary: COLORS.WHITE,
                tertiary: COLORS.GREY_900,
            });
        }

        case "danger": {
            return getFillTypeStyleFromColor(fillType, {
                primary: COLORS.RED,
                primaryDisabled: COLORS.RED_100,
                secondary: COLORS.WHITE,
                tertiary: COLORS.RED_100,
            });
        }
        case "success": {
            return getFillTypeStyleFromColor(fillType, {
                primary: COLORS.GREEN_700,
                primaryDisabled: COLORS.GREEN_300,
                secondary: COLORS.WHITE,
                tertiary: COLORS.GREEN_200,
            });
        }
        case "warning": {
            return getFillTypeStyleFromColor(fillType, {
                primary: COLORS.ORANGE_500,
                primaryDisabled: COLORS.ORANGE_100,
                secondary: COLORS.WHITE,
                tertiary: COLORS.ORANGE_100,
            });
        }
    }
};

const StyledButton = styled.button<StyledButtonProps>`
    border: none;
    cursor: pointer;
    overflow: hidden;
    transition: all 500ms;

    display: inline-flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    text-align: center;

    font-size: ${({ size }) => `${FontSize[size ?? "m"]}rem`};

    width: ${({ width, blockBtn }) =>
        blockBtn ? "100%" : width ? getSizeInRem(width) : "fit-content"};
    border-radius: ${({ isRounded }) => (isRounded ? "2em" : "4px")};
    padding: 0.75rem 1rem;

    ${({ color = "primary", fillType = "contained" }) =>
        getBtnStyle(color, fillType)};

    &:disabled {
        cursor: not-allowed;
    }
`;

export default StyledButton;
